import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import PouchDB from 'pouchdb-browser';

import { AuthService } from 'services/auth.service';
import { UserDb } from 'services/user-db.service';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { DocChange } from 'services/doc-watcher';
import { AppEnvironment } from 'environments/environment';
import { getLocalAdapterOpts } from './local-database-engine';
import {
  CustomExercise,
  exerciseDatabaseName,
  ImportedGlobalExercisesIds,
  importedGlobalExercisesIdsId,
  isCustomExerciseById,
} from '@cf-shared/data-model/exercise';
import { CoachRelationship, coachRelAsClientId } from '@cf-shared/data-model/coach';
import { isPouchConflict, isPouchNotFound } from '@cf-shared/data-model/pouch';
import { deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { computeAuthorIdentityHashAsync } from 'util/author-identity-hash';
import { setStagingDbHandle } from 'services/global-exercises-staging-handle';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { LoggingService } from 'services/logging.service';
import { createOncePerConditionReporter } from 'util/once-per-condition-reporter';
import {
  makeLocalAdapterFaultReporter, makeLocalCorruptionReporter,
} from './pouchdb-adapter-capacitor-sqlite/local-corruption-reporter';
import { PubSubService, PubSubRegistration } from 'services/pubsub.service';
import { ConnectionState, connectionStateChangedEvent } from 'services/online-state.service';
// Phase 3 of #3198 — pure gate helpers extracted to coach-exercise-sync-gates.ts so
// a regression test can pin the mutate-event detach path (DocWatcher fires a Changed
// event, not Deleted) without importing this whole Angular service.
import { shouldRunReplicationAForCoach, coachReplicationNeedsRestart, reportCoachSyncFailure, planCoachDocWrite, saveCoachCopyAsync } from 'services/coach-exercise-sync-gates';

// LocalStorage marker tying the on-disk staging DB to a userdb identity. PouchDB persists
// `global_exercises` in IndexedDB / capacitor-sqlite across sessions, so without this guard
// switching users (logout+login as different account, or coach entering manage mode and
// becoming a different effective userdb) would surface stale docs from the prior identity.
// On a mismatch we destroy the local DB before recreating — see Phase 2 PRD §8 (close-out commit 31db784b0).
const STAGING_DB_IDENTITY_KEY = 'coach-exercise-sync.stagingDbIdentity';

// URL-length cap on Replication B per plan §8 — CouchDB default `httpd/max_uri_length=8192`,
// each id ~52 chars, JSON-stringified array exceeds the cap around 150 ids. Keep only the
// most-recently-imported 100; older imports stay snapshotted in `userdb-*` but stop receiving
// live updates. Acceptable at typical scale.
const MAX_IMPORTED_IDS = 100;

// PouchDB.Replication / PouchDB.Changes types aren't surfaced cleanly through the
// `pouchdb-browser` type alias used elsewhere in this file. Until that's tightened
// project-wide, use a narrow alias that documents what we expect from the handle.
type CancelableHandle = { cancel: () => void; on?: (event: string, cb: (...args: any[]) => void) => void };

// Orchestrates filtered live replication from the remote `global_exercises` CouchDB into
// a local PouchDB staging mirror, plus the apply pipeline that copies coach-channel docs
// into the user's `userdb-*` automatically. Public-imported docs land in staging only;
// the `userdb-*` snapshot at last-Accepted revision is kept by the existing Phase 1 flow
// and updated via the diff dialog when the user explicitly Accepts.
//
// Lifecycle: `startAsync` post-auth in app.component, `stopAsync` on logout/manage-mode
// reload. The service tears down and recreates from scratch on identity change.
@Injectable({ providedIn: 'root' })
export class CoachExerciseSyncService {
  private localGlobalExercises: PouchDB | null = null;
  private remoteGlobalExercises: PouchDB | null = null;

  private replicationA: CancelableHandle | null = null; // coach channel
  private replicationB: CancelableHandle | null = null; // user-imported public shares
  private localChangesFeed: CancelableHandle | null = null; // PouchDB.changes() handle for apply pipeline

  private importedDocWatcher: DocWatcher | null = null; // user-DB DocWatcher on imported-ids singleton
  private coachRelDocWatcher: DocWatcher | null = null; // user-DB DocWatcher on coach-relationship/as-client

  private currentUserUid: string | undefined;
  private currentCoachUid: string | undefined;
  private currentImportedIds: string[] = [];

  // Single-writer queue for reconcile + DocChange handlers. All callers chain onto this
  // promise so teardown and restart never overlap. The queue resets to `Promise.resolve()`
  // after each task; failures are caught so one bad reconcile doesn't poison the chain.
  private reconcileQueue: Promise<void> = Promise.resolve();

  // Per-id "last applied source rev" map, written when the apply pipeline writes to
  // userdb-*. Used to skip re-applying a doc whose source rev is already current — covers
  // both the initial-replay vs live-feed echo (same rev arrives twice during cold start)
  // and idempotent rewrites on transient `_rev` mismatches in the staging DB.
  private lastAppliedSourceRevById = new Map<string, string>();

  // #6367: ids of coach copies whose last save did not land (it threw, or the wrapper's recovery
  // swallowed it). The shared in-memory object may then hold coach content the database never
  // received, so the next apply writes it even when it compares equal. Deliberately NOT cleared by
  // stopAsync: it must live exactly as long as those in-memory objects, which is the page's life.
  private coachCopyIdsWithUnlandedSave = new Set<string>();

  // Emits the coach exercise id each time the apply pipeline applies a coach doc into
  // userdb-* (i.e. a coach source changed). UI surfaces that track fork updates (the
  // exercise-switch "update available" dot/menu) subscribe to refresh live, without an
  // app reload — the coach-fork update path is a same-device local write, so it does NOT
  // flow through the cross-device-sync DocWatcher notifications.
  private readonly coachExerciseAppliedSubject = new Subject<string>();
  readonly coachExerciseApplied$ = this.coachExerciseAppliedSubject.asObservable();

  // In-flight apply-pipeline promises. The pipeline runs off the changes-feed `on('change')`
  // callback, which is INDEPENDENT of the reconcile queue — so stopAsync needs to track
  // these explicitly and await before closing handles, otherwise a mid-flight
  // `userDb.saveCustomExerciseAsync` can land after the userdb teardown.
  private inFlightApplies = new Set<Promise<void>>();

  // Coach-side: watcher on the coach's own coach-client/* slots. When any slot transitions
  // to Claimed mid-session (the coach gets a new client while the app is open), we sweep
  // owned UDEs and republish each one through the coach-channel impl so the new client
  // gets the coach's pre-existing library, not just UDEs the coach saves after the claim.
  // Also fires once on startup if `hasClaimedClientsAsync` is true to catch the
  // pre-Phase-2 / pre-first-claim shape (coach had UDEs before Phase 2 wired the auto-publish
  // trigger; doc-presence on `global_exercises` but no `coachShared` flag).
  private coachClientSlotsWatcher: DocWatcher | null = null;
  // Reentrancy guard for the sweep: avoid stacking sweeps if claims arrive in rapid
  // succession or the startup sweep overlaps with a slot-change sweep.
  private sweepInFlight: Promise<void> | null = null;
  // true from the moment stopAsync begins until the next startAsync. The sweep reads it
  // between exercises, so a sign-out does not wait for (or report) the rest of a sweep.
  private sweepStopRequested = false;

  // F6 (#3988): reconcile the replications whenever the app transitions
  // Offline->Online, so a longpoll that died silently across an offline/
  // background gap (#3862 class) is rebuilt. Stored so stopAsync can unsubscribe.
  private connectionStateSubscription: PubSubRegistration | null = null;

  // F6 (#3988): which replication error handlers have already reported to
  // Sentry this session. retry:true means the same dead link can emit errors
  // repeatedly, so we report each of A/B at most once — the volume signal
  // belongs in Mixpanel, not Sentry.
  private reportedReplicationErrors = new Set<'A' | 'B'>();

  // #5191: a teardown step (a replication cancel, the changes-feed cancel, a handle close) that
  // throws. A failed cancel can leave a replication running that nothing tracks any more, so the
  // failure is still unresolved when the catch ends. tearDownReplications runs on every
  // Offline->Online reconnect, so each step reports at most once per page load. Unlike
  // reportedReplicationErrors this is NOT re-armed by stopAsync: a sign-out and sign-in should
  // not file the same broken cancel again.
  private reportTeardownFailure = createOncePerConditionReporter((conditionName, err) => {
    const described = describeErrorForLog(err);
    console.error(conditionName, described);
    this.logging.trackSilentProblem(conditionName, { err: described });
  });

  constructor(
    private auth: AuthService,
    private userDb: UserDb,
    private cloudFunctions: CloudFunctionsService,
    private logging: LoggingService,
    private pubSub: PubSubService,
  ) {}

  async startAsync(): Promise<void> {
    if (!this.userDb.database) {
      console.log('coach-exercise-sync: skipping start — userDb not initialized');
      return;
    }
    if (!this.auth.user?.uid) {
      console.log('coach-exercise-sync: skipping start — no authed user');
      return;
    }

    this.currentUserUid = this.auth.user.uid;
    this.sweepStopRequested = false;

    // Identity guard — if the on-disk staging DB belongs to a different user, wipe it
    // before re-opening. See STAGING_DB_IDENTITY_KEY note above.
    const userdbIdentity = this.deriveUserdbIdentity();
    const previousIdentity = localStorage.getItem(STAGING_DB_IDENTITY_KEY);
    if (previousIdentity && previousIdentity !== userdbIdentity) {
      console.log(`coach-exercise-sync: userdb identity changed (${previousIdentity} -> ${userdbIdentity}); destroying stale staging DB`);
      const stale = new PouchDB(exerciseDatabaseName, getLocalAdapterOpts(
        makeLocalCorruptionReporter(this.logging, 'coach-exercise-staging'),
        makeLocalAdapterFaultReporter(this.logging, 'coach-exercise-staging'),
      ));
      // This is NOT `DatabaseWrapper.localDb`, so it is one of the two destroy sites deliberately
      // outside the `beginPurge` bracket — why that is safe is written once, after `endPurge` in
      // `database-recovery.service.ts`. The site-specific part: here there is nothing for the
      // bracket to protect at all. The handle is constructed on the lines just above, is reachable
      // from nowhere else, and has never been read or written, so no operation can be in flight to
      // reject when the store goes; `stopAsync` has already `close()`d the previous handle and
      // cleared the module-level staging reference.
      try {
        await stale.destroy();
      } catch (err: any) {
        // The wipe failed, so the previous identity's documents are STILL on disk. Do not
        // open that database and do not write the identity marker: writing it would claim
        // the leftover documents for the current identity, and the guard above is the only
        // thing that would ever retry the wipe — once the marker says they are ours, it can
        // never fire for them again. Leaving the marker at the previous value means the next
        // startAsync sees the same mismatch and retries, which is the same protection the
        // comment below already spells out for a failed construction.
        // What this costs the session: design-docs/coach-mode.md, "Manage mode".
        console.log('coach-exercise-sync: failed to destroy stale staging DB', err);
        this.logging.trackSilentProblem('coach-exercise-sync: failed to destroy stale staging DB', {
          previousIdentity,
          userdbIdentity,
          // describeErrorForLog, not String(err): a rejected PouchDB destroy carries the
          // {status, error, reason} shape, which String() renders as [object Object].
          err: describeErrorForLog(err),
        });
        // Nothing was started, so leave no half-started state behind for stopAsync to unwind.
        this.currentUserUid = undefined;
        return;
      }
    }

    // Open the staging DB BEFORE persisting the identity marker. If construction throws,
    // the marker stays at the previous value and the next start will still see the
    // identity mismatch and wipe correctly.
    this.localGlobalExercises = new PouchDB(exerciseDatabaseName, {
      auto_compaction: true,
      ...getLocalAdapterOpts(
        makeLocalCorruptionReporter(this.logging, 'coach-exercise-staging'),
        makeLocalAdapterFaultReporter(this.logging, 'coach-exercise-staging'),
      ),
    });
    localStorage.setItem(STAGING_DB_IDENTITY_KEY, userdbIdentity);
    setStagingDbHandle(this.localGlobalExercises);

    this.remoteGlobalExercises = new PouchDB(
      AppEnvironment.pouchConfig.couch_serverurl + exerciseDatabaseName,
      { fetch: this.auth.getFetchFunction() },
    );

    // Read initial replication inputs.
    this.coachRelDocWatcher = this.userDb.database.createDocWatcher((changes) => this.handleCoachRelChanges(changes));
    try {
      const coachRel = await this.userDb.database.getTypedRecordAsync<CoachRelationship>(coachRelAsClientId, this.coachRelDocWatcher);
      this.currentCoachUid = coachRel?.coachUid;
    } catch (err) {
      // df-970b5092487d: a not-found is expected — a user with no coach never had this doc.
      // Anything else means we do not know who this user's coach is, so no coach-channel
      // replication runs for the rest of the session and only the next change to the doc
      // would put it right. That is unresolved when this line runs, so it is reported the
      // same way handleCoachRelChanges reports its own read failure.
      if (!isPouchNotFound(err)) {
        const described = describeErrorForLog(err);
        console.error('coach-exercise-sync: failed to read coach-relationship/as-client', described);
        this.logging.trackSilentProblem('coach-exercise-sync: startup read of coach-relationship/as-client failed', { err: described });
      }
    }

    this.importedDocWatcher = this.userDb.database.createDocWatcher((changes) => this.handleImportedIdsChanges(changes));
    try {
      const importedDoc = await this.userDb.database.getTypedRecordAsync<ImportedGlobalExercisesIds>(importedGlobalExercisesIdsId, this.importedDocWatcher);
      this.currentImportedIds = importedDoc?.ids ?? [];
    } catch (err) {
      // df-970b5092487d: same split as the coach-relationship read above. A not-found is
      // expected (a user who imported nothing never had this singleton); anything else
      // leaves the imported list empty for the session, so the public shares this user
      // imported stop receiving live updates until the doc next changes.
      if (!isPouchNotFound(err)) {
        const described = describeErrorForLog(err);
        console.error('coach-exercise-sync: failed to read imported-ids singleton', described);
        this.logging.trackSilentProblem('coach-exercise-sync: startup read of imported-ids singleton failed', { err: described });
      }
    }

    // Apply pipeline first (replay any docs already in the staging DB before live feed
    // starts), THEN replications. Sequencing avoids the cold-start race where the live
    // changes feed and the initial-replay loop both write to the same userdb-* doc.
    await this.attachApplyPipelineAsync();
    await this.reconcileReplicationsAsync();

    // F6 (#3988): replication longpolls can die silently across offline/background
    // gaps (#3862 class). The DatabaseWrapper DBs get ensureSyncsActive on
    // reconnect; this DB lives outside that machinery, so rebuild its replications
    // on every Offline->Online transition. reconcileReplicationsAsync is idempotent
    // and queue-serialized, so a healthy pair is simply torn down and recreated —
    // cheap for this small DB. Unsubscribe any prior registration first so a repeat
    // startAsync (userdb identity change) does not stack subscriptions.
    this.connectionStateSubscription?.unsubscribe();
    this.connectionStateSubscription = this.pubSub.subscribe(connectionStateChangedEvent, (state) => {
      if (state === ConnectionState.Online) void this.reconcileReplicationsAsync();
    });

    // Coach-side: watch own coach-client/* slots so claims that happen mid-session
    // trigger a sweep of the coach's existing UDEs into the coach channel. Also fire a
    // sweep right now if the coach already has claimed clients (handles the
    // pre-Phase-2 / pre-first-claim shape where prior UDEs were never auto-published).
    // DocWatcher callbacks fire for ANY doc change broadcast on the database, not only
    // the docs registered with the watcher. Filter to coach-client/* changes here —
    // without the filter every UDE save / log entry / meal save fires a sweep, which
    // would do a full hasClaimedClientsAsync + getAllCustomExercisesAsync iteration
    // even though it's idempotent on the server. Mirrors handleCoachRelChanges and
    // handleImportedIdsChanges which filter by exact id.
    this.coachClientSlotsWatcher = this.userDb.database.createDocWatcher((changes) => {
      if (!changes.some((c) => c.id.startsWith('coach-client/'))) return;
      void this.maybeSweepOwnedUdesAsync('slot-change');
    });
    try {
      await this.userDb.database.getByIdHeadAsync('coach-client/', this.coachClientSlotsWatcher);
    } catch (err) {
      // without the watcher, a coach who gains a client mid-session gets no automatic publish of
      // their own exercises to that client until the next start (#5191)
      const described = describeErrorForLog(err);
      console.error('coach-exercise-sync: failed to register coach-client slot watcher', described);
      this.logging.trackSilentProblem('coach-exercise-sync: coach-client slot watcher registration failed', { err: described });
    }
    void this.maybeSweepOwnedUdesAsync('startup');

    console.log(`coach-exercise-sync: started (coachUid=${this.currentCoachUid ?? 'none'}, importedIds=${this.currentImportedIds.length})`);
  }

  async stopAsync(): Promise<void> {
    // tell a running sweep to finish the exercise it is on and stop
    this.sweepStopRequested = true;
    // Stop the async reconcile TRIGGERS before draining the queue, so no new
    // reconcile can be enqueued after we drain and tear down. Otherwise an event
    // landing during the drain (an Offline->Online flip via the F6 subscription)
    // would enqueue a reconcile that runs
    // after teardown and rebuilds replications against handles about to close.
    // F6 (#3988): drop the Offline->Online reconcile subscription.
    this.connectionStateSubscription?.unsubscribe();
    this.connectionStateSubscription = null;

    // Drain the queue first so an in-flight reconcile finishes before we tear down.
    await this.reconcileQueue.catch(() => undefined);

    this.tearDownReplications();
    if (this.localChangesFeed) {
      try {
        this.localChangesFeed.cancel();
      } catch (err) {
        this.reportTeardownFailure('coach-exercise-sync: changes-feed cancel failed', err);
      }
      this.localChangesFeed = null;
    }
    // Drain in-flight applies BEFORE closing the local DB / userDb handles. The apply
    // pipeline runs off the changes-feed callback (independent of `reconcileQueue`), so
    // a mid-flight `userDb.saveCustomExerciseAsync` can otherwise land after teardown.
    if (this.inFlightApplies.size > 0) {
      await Promise.allSettled([...this.inFlightApplies]);
    }
    // Drain any in-flight sweep too — its loop dereferences userDb/database, so a
    // mid-sweep stopAsync must not leave a publish call racing the teardown.
    if (this.sweepInFlight) {
      await this.sweepInFlight.catch(() => undefined);
    }
    this.coachRelDocWatcher?.destroy();
    this.coachRelDocWatcher = null;
    this.importedDocWatcher?.destroy();
    this.importedDocWatcher = null;
    this.coachClientSlotsWatcher?.destroy();
    this.coachClientSlotsWatcher = null;
    // reset the once-per-session error-report guard so a fresh start re-arms it
    // (the subscription was already unsubscribed at the top)
    this.reportedReplicationErrors.clear();
    if (this.remoteGlobalExercises) {
      try {
        await this.remoteGlobalExercises.close();
      } catch (err) {
        this.reportTeardownFailure('coach-exercise-sync: remote handle close failed', err);
      }
      this.remoteGlobalExercises = null;
    }
    // Close (NOT destroy) the local handle — data is meant to persist across normal app
    // restarts so coach UDEs are immediately available offline. The identity-guard in
    // startAsync handles wiping when the userdb identity changes. close() releases the
    // in-memory cursors and event listeners; destroy() would wipe IDB.
    setStagingDbHandle(null);
    if (this.localGlobalExercises) {
      try {
        await this.localGlobalExercises.close();
      } catch (err) {
        this.reportTeardownFailure('coach-exercise-sync: local handle close failed', err);
      }
      this.localGlobalExercises = null;
    }
    this.lastAppliedSourceRevById.clear();
    this.inFlightApplies.clear();
    this.currentCoachUid = undefined;
    this.currentImportedIds = [];
    this.currentUserUid = undefined;
  }

  // ---- Internals ----

  private deriveUserdbIdentity(): string {
    // Manage mode: the active userdb is the managed client's, not the coach's. Compose
    // identity from authed uid + managed-client uid so we wipe staging when the user
    // enters or leaves manage mode (each transition reloads the app — see plan §8).
    const authUid = this.currentUserUid ?? 'unknown';
    const managed = this.userDb.getCoachManagingClient?.()?.clientUid;
    return managed ? `${authUid}:managing:${managed}` : authUid;
  }

  // Public entry point. Chains onto the single-writer queue so concurrent callers (coachUid
  // change, imported-list change, reconnect) execute strictly in order — no teardown
  // racing a restart.
  private reconcileReplicationsAsync(): Promise<void> {
    const next = this.reconcileQueue.then(() => this.doReconcileReplicationsAsync());
    // Catch failures so one bad reconcile doesn't poison subsequent enqueues.
    this.reconcileQueue = next.catch((err) => {
      console.error('coach-exercise-sync: reconcileReplicationsAsync failed', err);
      this.logging.trackSilentProblem('coach-exercise-sync: reconcileReplicationsAsync failed', { err: String(err?.message ?? err), stack: err?.stack });
    });
    return next;
  }

  private async doReconcileReplicationsAsync(): Promise<void> {
    this.tearDownReplications();
    if (!this.localGlobalExercises || !this.remoteGlobalExercises) return;

    // Replication A — coach channel. Active when the user is coached.
    // Phase 3: gate is on coachUid truthiness via `shouldRunReplicationAForCoach`,
    // NOT on a previous delete-event from DocWatcher. Detach now mutates the doc
    // (clears coachUid) rather than deleting it.
    if (shouldRunReplicationAForCoach(this.currentCoachUid)) {
      const coachIdentityHash = await computeAuthorIdentityHashAsync(this.currentCoachUid);
      this.replicationA = this.localGlobalExercises.replicate.from(this.remoteGlobalExercises, {
        live: true,
        retry: true,
        filter: 'access/byCoachAuthorIdentityHash',
        query_params: { authorIdentityHash: coachIdentityHash },
        style: 'main_only',
      }) as CancelableHandle;
      this.replicationA.on?.('error', (err: any) => this.reportReplicationError('A', err));
    }

    // Replication B — user-imported public shares. Capped at MAX_IMPORTED_IDS most recent.
    if (this.currentImportedIds.length > 0) {
      const ids = this.currentImportedIds.slice(-MAX_IMPORTED_IDS);
      this.replicationB = this.localGlobalExercises.replicate.from(this.remoteGlobalExercises, {
        live: true,
        retry: true,
        filter: 'access/byImportedIds',
        query_params: { importedIds: JSON.stringify(ids) },
        style: 'main_only',
      }) as CancelableHandle;
      this.replicationB.on?.('error', (err: any) => this.reportReplicationError('B', err));
    }
  }

  // F6 (#3988): report a replication error to Sentry once per session per
  // replication (A/B). Previously these errors were console.log-only, so a
  // silently-dead longpoll left no telemetry. retry:true keeps re-emitting on
  // the same dead link, hence the once-per-session guard.
  private reportReplicationError(which: 'A' | 'B', err: any) {
    console.log(`coach-exercise-sync: replication ${which} error`, err);
    if (this.reportedReplicationErrors.has(which)) return;
    this.reportedReplicationErrors.add(which);
    this.logging.trackSilentProblem('coach-exercise-sync: replication error', {
      which, name: err?.name, message: err?.message, status: err?.status,
    });
  }

  // Cancel any active replication handles. Safe to call when nothing is running.
  private tearDownReplications() {
    if (this.replicationA) {
      try {
        this.replicationA.cancel();
      } catch (err) {
        this.reportTeardownFailure('coach-exercise-sync: replication A cancel failed', err);
      }
      this.replicationA = null;
    }
    if (this.replicationB) {
      try {
        this.replicationB.cancel();
      } catch (err) {
        this.reportTeardownFailure('coach-exercise-sync: replication B cancel failed', err);
      }
      this.replicationB = null;
    }
  }

  // Trailing `.catch` on the queue assignment is REQUIRED — without it, an unexpected
  // throw outside the inner try (e.g. `this.userDb.database!` evaluating to null after a
  // concurrent stopAsync) leaves `this.reconcileQueue` in a rejected state, and every
  // subsequent `.then(...)` enqueue silently skips its callback. The catch keeps the
  // queue "live" by always settling to undefined.
  private handleCoachRelChanges(changes: DocChange[]) {
    for (const change of changes) {
      if (change.id !== coachRelAsClientId) continue;
      // Chain onto the reconcile queue so identity reads + reconcile run as one atomic unit
      // and never race a concurrent imported-ids change.
      this.reconcileQueue = this.reconcileQueue.then(async () => {
        if (!this.userDb.database) return; // post-stop arrival
        let coachUid: string | undefined;
        try {
          const doc = await this.userDb.database.withDocWatcherAsync(w => this.userDb.database.getTypedRecordAsync<CoachRelationship>(coachRelAsClientId, w));
          coachUid = doc?.coachUid;
        } catch (err) {
          if (!isPouchNotFound(err)) throw err;
        }
        if (coachReplicationNeedsRestart(this.currentCoachUid, coachUid)) {
          console.log(`coach-exercise-sync: coachUid changed ${this.currentCoachUid} -> ${coachUid}`);
          this.currentCoachUid = coachUid;
          await this.doReconcileReplicationsAsync();
        }
      }).catch((err) => {
        // df-330d6792ded0: during initial sync the re-read can pass through to the remote
        // server, so a network drop is routine here and is not sent to Sentry; currentCoachUid
        // stays as it was until the doc next changes or the app next starts. See reportCoachSyncFailure.
        reportCoachSyncFailure((name, ctx) => this.logging.trackSilentProblem(name, ctx), 'coach-exercise-sync: handleCoachRelChanges failed', err, {},
          'the next change to the document or the next app start re-reads it');
      });
    }
  }

  private handleImportedIdsChanges(changes: DocChange[]) {
    for (const change of changes) {
      if (change.id !== importedGlobalExercisesIdsId) continue;
      // The DocChangeType only exposes `Changed` / `New`, but the doc can still be gone when
      // the read below runs — deleting the user's data removes it and announces the change,
      // and a new user never had one stored. So a missing doc reads as an empty list, the
      // same way handleCoachRelChanges and the startup read in startAsync treat theirs.
      // Chain onto the reconcile queue (same reasoning as handleCoachRelChanges).
      this.reconcileQueue = this.reconcileQueue.then(async () => {
        if (!this.userDb.database) return; // post-stop arrival
        let doc: ImportedGlobalExercisesIds | undefined;
        try {
          doc = await this.userDb.database.withDocWatcherAsync(w => this.userDb.database.getTypedRecordAsync<ImportedGlobalExercisesIds>(importedGlobalExercisesIdsId, w));
        } catch (err) {
          if (!isPouchNotFound(err)) throw err;
        }
        const newIds = doc?.ids ?? [];
        // Only restart replication if the trimmed (cap-applied) list actually changed.
        // PouchDB doesn't support changing filter params mid-stream, so we cancel + restart.
        const oldKey = JSON.stringify(this.currentImportedIds.slice(-MAX_IMPORTED_IDS));
        const newKey = JSON.stringify(newIds.slice(-MAX_IMPORTED_IDS));
        this.currentImportedIds = newIds;
        if (oldKey !== newKey) {
          await this.doReconcileReplicationsAsync();
        }
      }).catch((err) => {
        // df-330d6792ded0: same as handleCoachRelChanges, a network drop on the re-read is
        // routine during initial sync and is not sent to Sentry. See reportCoachSyncFailure.
        reportCoachSyncFailure((name, ctx) => this.logging.trackSilentProblem(name, ctx), 'coach-exercise-sync: handleImportedIdsChanges failed', err, {},
          'the next change to the document or the next app start re-reads it');
      });
    }
  }

  // Tracks an apply-pipeline promise so stopAsync can drain it. Auto-removes from the set
  // when the promise settles. Failures are already logged inside applyChangeAsync; we
  // only need to keep the tracking from leaking unhandled rejections.
  private trackInFlightApply(p: Promise<void>): Promise<void> {
    this.inFlightApplies.add(p);
    return p.finally(() => this.inFlightApplies.delete(p));
  }

  // Replays every doc currently in the staging DB through the apply pipeline, THEN
  // attaches the live changes feed. Sequencing matters: if the live feed were attached
  // first (with `since:'now'`), it would race the replay loop on every doc that arrives
  // mid-replay, and both branches could write to the same userdb-* doc concurrently.
  // The replay handles cold-start consistency — if the app crashed between replication
  // landing a doc and the apply pipeline saving it, the next start re-tries idempotently.
  private async attachApplyPipelineAsync(): Promise<void> {
    if (!this.localGlobalExercises) return;

    try {
      const all = await this.localGlobalExercises.allDocs({ include_docs: true });
      for (const row of all.rows) {
        if (!row.doc) continue;
        await this.applyChangeAsync(row.doc as unknown as CustomExercise, false);
      }
    } catch (err: any) {
      // Initial-replay failure silently disables the apply pipeline for the rest of the
      // session (the live feed below will pick up new changes, but the catch-up backlog
      // from staging stays unapplied). Surface so a corrupt staging DB or transient
      // PouchDB I/O error isn't invisible.
      console.error('coach-exercise-sync: initial apply replay failed', err);
      this.logging.trackSilentProblem('coach-exercise-sync: initial apply replay failed', { err: String(err?.message ?? err), stack: err?.stack });
    }

    // Now that the replay drained, install the live feed. since:'now' avoids re-emitting
    // historical changes the replay just handled.
    this.localChangesFeed = this.localGlobalExercises.changes({
      since: 'now',
      live: true,
      include_docs: true,
    }) as CancelableHandle;
    this.localChangesFeed.on?.('change', (change: any) => {
      void this.trackInFlightApply(this.applyChangeAsync(change.doc, !!change.deleted));
    });
    this.localChangesFeed.on?.('error', (err: any) => {
      console.log('coach-exercise-sync: apply pipeline error', err);
    });
  }

  private async applyChangeAsync(doc: CustomExercise, deleted: boolean): Promise<void> {
    if (!doc) return;
    if (deleted) {
      // Phase 2 leaves prior copies in userdb-* alone; Phase 3 handles post-detach cleanup.
      // Also covers compaction tombstones (auto_compaction emits deleted events).
      this.lastAppliedSourceRevById.delete(doc._id);
      return;
    }
    if (!this.userDb.database) return;
    if (!this.currentCoachUid) return;

    const coachIdentityHash = await computeAuthorIdentityHashAsync(this.currentCoachUid);
    const isCoachDoc = !!doc.local?.coachShared && doc.authorIdentityHash === coachIdentityHash;
    if (!isCoachDoc) return; // public-imports stay in staging — Phase 1 banner/diff dialog handles them.

    // Skip if we've already applied this exact source rev — handles initial-replay vs
    // live-feed echo, and any duplicate emission from the changes feed.
    if (this.lastAppliedSourceRevById.get(doc._id) === doc._rev) return;

    await this.applyCoachDocAsync(doc);
    // notify live-update subscribers (exercise-switch dot/menu) that a coach source
    // changed. Emitting on a failed apply is harmless — subscribers just re-check.
    this.coachExerciseAppliedSubject.next(doc._id);
  }

  private async applyCoachDocAsync(stagingDoc: CustomExercise): Promise<void> {
    // getCustomExerciseByIdAsync THROWS isPouchNotFound on missing docs (does not return
    // null). Translate to a typed nullable here; let any other error propagate.
    let canonical: CustomExercise | null;
    try {
      canonical = await this.userDb.getCustomExerciseByIdAsync(stagingDoc._id);
    } catch (err: any) {
      if (isPouchNotFound(err)) {
        canonical = null;
      } else {
        // during initial sync this read can pass through to the remote server, so a network drop
        // is expected here (df-ae300b22d281). The source rev is not recorded as applied, so the
        // replay at the next app start applies this doc again; any other failure is reported.
        reportCoachSyncFailure((name, ctx) => this.logging.trackSilentProblem(name, ctx), 'coach-exercise-sync: getCustomExerciseByIdAsync failed', err,
          { docId: stagingDoc._id }, 'the next app start re-applies it');
        return;
      }
    }

    try {
      await this.writeCoachDocIntoUserDbAsync(stagingDoc, canonical);
    } catch (err: any) {
      // 409 conflict → reload and retry once. If still conflicting, log and drop —
      // the next replication tick will re-apply.
      if (isPouchConflict(err)) {
        await this.retryApplyOn409Async(stagingDoc, err);
        return;
      }
      // Terminal: drop this apply attempt — the next changes-feed event for this doc
      // will retry. Surface to Sentry so prod failures aren't invisible.
      console.error('coach-exercise-sync: applyCoachDocAsync failed', err);
      this.logging.trackSilentProblem('coach-exercise-sync: applyCoachDocAsync failed', { docId: stagingDoc._id, err: String(err?.message ?? err), stack: err?.stack });
    }
  }

  // The one write of a coach doc into userdb-*, shared by the first attempt and the 409 retry so a
  // new step in this sequence (like the fork-update stamp) cannot be added to one path and missed
  // on the other. Errors propagate: each caller owns its own conflict and failure handling.
  private async writeCoachDocIntoUserDbAsync(stagingDoc: CustomExercise, existing: CustomExercise | null): Promise<void> {
    const lastSaveLanded = !this.coachCopyIdsWithUnlandedSave.has(stagingDoc._id);
    const toSave = planCoachDocWrite(stagingDoc, existing, lastSaveLanded);
    if (!toSave) {
      // if the copy already equals the coach version, skip the save (#6367): the replay at
      // every app start would otherwise add a revision to every coach exercise
      this.lastAppliedSourceRevById.set(stagingDoc._id, stagingDoc._rev);
      return;
    }
    let target = toSave;
    if (existing) {
      // existing copy: deep-overwrite preserves the identity-map reference that other
      // DocWatchers (picker, edit-exercise, exercise-stats) hold. The overlay keeps the local
      // _rev so userdb-* sequencing stays clean (we're mutating the local copy, not adopting
      // the remote rev).
      deepOverwriteCouchSafe(existing, toSave);
      target = existing;
    }
    // first arrival saves the clone, which carries no _rev, so PouchDB assigns userdb-* its own.
    // if the save does not land, remember it so the next apply writes instead of skipping
    await saveCoachCopyAsync(stagingDoc._id, this.coachCopyIdsWithUnlandedSave,
      () => this.userDb.saveCustomExerciseAsync(target, DatabaseWrapper.getTransactionId()));
    this.lastAppliedSourceRevById.set(stagingDoc._id, stagingDoc._rev);
  }

  // Single 409-retry. The reload may itself throw isPouchNotFound (the canonical was
  // deleted between original save and reload); in that case re-save as first-arrival.
  private async retryApplyOn409Async(stagingDoc: CustomExercise, originalErr: unknown): Promise<void> {
    try {
      let reloaded: CustomExercise | null;
      try {
        reloaded = await this.userDb.getCustomExerciseByIdAsync(stagingDoc._id);
      } catch (err) {
        if (isPouchNotFound(err)) reloaded = null;
        else throw err;
      }
      // if canonical was deleted between original save and reload, this re-saves as first-arrival
      await this.writeCoachDocIntoUserDbAsync(stagingDoc, reloaded);
    } catch (retryErr: any) {
      // Terminal: drop this 409-retry attempt — the next changes-feed event for this doc
      // will re-enter applyCoachDocAsync. Surface to Sentry so 409-retry-exhausted cases
      // (which indicate a persistent conflict on userdb-* this user never resolves) aren't
      // invisible in prod.
      console.error('coach-exercise-sync: 409 retry failed; dropping (next replication tick will retry)', retryErr, 'original error:', originalErr);
      this.logging.trackSilentProblem('coach-exercise-sync: 409 retry failed', {
        docId: stagingDoc._id,
        retryErr: String(retryErr?.message ?? retryErr),
        originalErr: String((originalErr as any)?.message ?? originalErr),
      });
    }
  }

  // Coach-side sweep: republish every owned, non-deleted UDE through the coach-channel
  // publish impl. Idempotent on the server — already-coach-shared docs short-circuit via
  // the lineage/flag-unchanged + deepCompareEQ check in publishExerciseToCoachChannel_impl
  // without a `_rev` bump, so existing replicating clients don't get spurious updates.
  //
  // Fires from two trigger points (see startAsync):
  //   - 'startup': on every coach app start when hasClaimedClientsAsync is true. Handles
  //     the case where the coach had UDEs predating Phase 2's auto-publish trigger.
  //   - 'slot-change': when a coach-client/* slot changes (e.g. a brand-new claim
  //     completes mid-session). New clients get the coach's full library, not just
  //     UDEs the coach saves after the claim.
  //
  // Reentrancy guard: chained via `sweepInFlight` so back-to-back triggers serialize.
  // Per-UDE error is logged + skipped — one failure doesn't block the rest of the sweep.
  private maybeSweepOwnedUdesAsync(trigger: 'startup' | 'slot-change'): Promise<void> {
    const next = (this.sweepInFlight ?? Promise.resolve()).then(() => this.doSweepOwnedUdesAsync(trigger));
    this.sweepInFlight = next.catch((err) => {
      // a network drop is expected here and retried by the next sweep; see reportCoachSyncFailure
      reportCoachSyncFailure((name, ctx) => this.logging.trackSilentProblem(name, ctx), 'coach-exercise-sync: sweep failed', err, { trigger }, 'the next sweep retries');
    });
    return next;
  }

  // True once the sweep has nobody to publish for: stopAsync has begun, or the user is
  // already signed out (sign-out reaches the auth state a moment before it reaches stopAsync).
  private isSweepBeingAbandoned(): boolean {
    return this.sweepStopRequested || !this.auth.user?.uid;
  }

  private async doSweepOwnedUdesAsync(trigger: 'startup' | 'slot-change'): Promise<void> {
    if (!this.userDb.database) return;
    const uid = this.currentUserUid;
    if (!uid) return;

    // Server enforces the same gate authoritatively, but skipping early avoids spamming
    // the function with rejections the coach can do nothing about.
    if (!(await this.userDb.hasClaimedClientsAsync())) return;

    const udes = await this.userDb.getAllCustomExercisesAsync();
    let published = 0;
    let skipped = 0;
    for (const ude of udes) {
      if (ude.virtuallyDeleted) continue;
      if (!isCustomExerciseById(ude._id)) continue;
      // Ownership matches the in-app `isOwnedByCurrentUser` check: hashSource prefix === uid.
      // UDEs without hashSource (e.g. coach UDEs auto-applied from someone ELSE's coach
      // channel via the apply pipeline) are intentionally skipped — the current user
      // doesn't own them and can't republish them anyway.
      const hashUid = ude.local?.hashSource?.split(':')[0];
      if (hashUid !== uid) {
        skipped++;
        continue;
      }
      // Cheap skip when local already says coachShared: true (round-tripped from server).
      if (ude.local?.coachShared === true) {
        skipped++;
        continue;
      }
      // if the service is stopping or the user has signed out, stop here. Every further
      // publish would fail for want of a signed-in user, and the next start sweeps again.
      if (this.isSweepBeingAbandoned()) break;
      try {
        const result = await this.cloudFunctions.publishExerciseToCoachChannelAsync({ exercise: ude });
        if (result?.error) {
          // Server-side rejection comes back as a 200-OK `{error}` body, not a throw.
          // "no claimed clients" is the one rejection that is expected rather than a
          // fault: a 'slot-change' sweep fires when the coach removes their last client,
          // and the guard at the top of this method reads the LOCAL coach-client/* slots,
          // which still show a claimed client while that removal is syncing. So the sweep
          // runs and the server — the authoritative check — refuses. Log it and move on,
          // the same way the save path does (see edit-exercise-page.ts). Every other
          // rejection (auth / schema / hashSource drift) still surfaces to Sentry, because
          // a persistent one would otherwise be invisible.
          const isExpectedSyncLag = result.error.includes('Coach channel requires at least one claimed client');
          if (isExpectedSyncLag) {
            console.log(`coach-exercise-sync: sweep publish skipped (sync-lag: no claimed clients yet) for ${ude._id}`, result.error);
            skipped++;
          } else {
            console.error(`coach-exercise-sync: sweep publish rejected by server for ${ude._id}`, result.error);
            this.logging.trackSilentProblem('coach-exercise-sync: sweep publish rejected by server', { docId: ude._id, trigger, serverError: result.error });
          }
        } else {
          published++;
        }
      } catch (err) {
        // if the user signed out while this publish was on its way, its failure is the
        // sign-out and not a fault: say so in the console and stop, without a report
        if (this.isSweepBeingAbandoned()) {
          console.log(`coach-exercise-sync: sweep (${trigger}) stopped at sign-out; the next start sweeps again`);
          break;
        }
        // Reached only on network / runtime errors. Skip this UDE but keep sweeping. A network
        // drop is not reported (the next sweep retries it); see reportCoachSyncFailure.
        reportCoachSyncFailure((name, ctx) => this.logging.trackSilentProblem(name, ctx), 'coach-exercise-sync: sweep publish failed', err, { docId: ude._id, trigger }, 'the next sweep retries');
      }
    }
    if (published > 0) {
      console.log(`coach-exercise-sync: sweep (${trigger}) published ${published} UDE(s) to coach channel (${skipped} skipped)`);
    }
  }
}
